/**
 * @description Scene manager handles the different scenes in the viewer.
 */

import { Color } from 'three';

import { publish, SubscriptionTopic } from './messaging/pubsub';
import { ProjectGeodataManager } from './project-geodata-manager';
import { Scene } from './scenes/scene';
import { SkyviewPanoramaScene } from './scenes/skyview-panorama-scene';
import { SkyviewScene, SkyviewSceneOptions } from './scenes/skyview-scene';

const defaultClearColor = new Color(0, 0, 0);

/** The scenes: SkyView's own, also the CAD viewer's, see ViewSettings.isCadViewer. */
export type SceneType = 'skyviewScene' | 'panoramaViewer';

interface AvailableScenes {
  [key: string]: Scene;
}

export type SceneManagerOptions = SkyviewSceneOptions;

const defaultOptions: SceneManagerOptions = {
  defaultTimelineContainerId: undefined,
  defaultSceneType: 'orthoScene',
};

export class SceneManager {
  static get instance(): SceneManager {
    return SceneManager._instance;
  }

  clearColor: Color = defaultClearColor;

  private static _instance: SceneManager = new SceneManager();

  private scenes: AvailableScenes;
  private _previousScene?: SceneType;
  private activeScene?: Scene;
  private options: SceneManagerOptions;

  get previousSceneType() {
    return this._previousScene;
  }

  private constructor() {
    this.scenes = {};

    this.options = defaultOptions;
  }

  /**
   *  Initialize the scene manager, adding all of the scenes
   */
  init(options?: SceneManagerOptions) {
    // Not the last SkyView's, e.g. the timeline of the shared view it showed
    this.options = { ...defaultOptions, ...options };
    // A scene left active, e.g. by a SkyView that wasn't cleared, would go on following the view
    // settings
    this.activeScene?.deactivate();
    this.activeScene = undefined;

    this.scenes = {
      skyviewScene: new SkyviewScene(this.options),
      panoramaViewer: new SkyviewPanoramaScene(),
    };
  }

  /**
   *  Set the scene based on its string id
   */
  async setScene(scene: SceneType) {
    // If the scene is already activated we don't need to do anything.
    if (this.getActiveSceneType() === scene) {
      return;
    }

    if (!this.isValidScene(scene)) {
      return;
    }

    publish(SubscriptionTopic.OnSceneChangeStarted, { scene });
    const newScene = this.scenes && this.scenes[scene];
    if (!newScene) {
      return;
    }

    await newScene.init();
    if (this.activeScene) {
      this.activeScene.deactivate();
      this._previousScene = this.getActiveSceneType();
    }

    this.activeScene = newScene;
    await this.activeScene.activate();

    publish(SubscriptionTopic.OnSceneChangeCompleted, { scene });
  }

  /**
   *  TypeGuard to ensure scene is a SceneType
   */
  isValidScene(scene: SceneType): scene is SceneType {
    const validScenes: SceneType[] = ['skyviewScene', 'panoramaViewer'];
    return validScenes.includes(scene);
  }

  /**
   * Shows the timeline container in the SkyView scene, which is shown again if another scene, such
   * as the panorama viewer, is.
   */
  async reInit(timelineContainerIdOrIndex: string | number) {
    const timelineContainerId =
      typeof timelineContainerIdOrIndex === 'string'
        ? timelineContainerIdOrIndex
        : ProjectGeodataManager.instance().getTimelineContainerIdByIndex(
            timelineContainerIdOrIndex,
          );
    // Also a container without data, which keeps the scene's origo. Loads once the scene is shown.
    const loading = (this.scenes.skyviewScene as SkyviewScene).loadTimelineContainer(
      timelineContainerId,
    );
    await this.setScene('skyviewScene');
    try {
      await loading;
    } catch (err) {
      console.error(err);
    }
  }

  /**
   *  Returns the scene matching the sceneId
   */
  getScene(sceneId: string): Scene {
    return this.scenes[sceneId];
  }

  /**
   *  Returns the current active scene
   */
  getActiveScene(): Scene | undefined {
    return this.activeScene;
  }

  /**
   *  Returns the current active scene
   */
  getActiveSceneType(): SceneType | undefined {
    return (
      this.activeScene &&
      (Object.keys(this.scenes).find((key) => {
        return this.scenes[key] === this.activeScene;
      }) as SceneType | undefined)
    );
  }

  /**
   *  Returns if the sceneId is active
   */
  isSceneActive(sceneId: string): boolean {
    return !!this.activeScene && this.activeScene === this.scenes[sceneId];
  }

  /**
   *  Animate the current scene
   */
  animate() {
    if (!this.activeScene) {
      return;
    }

    this.activeScene.animate();
  }

  /**
   *  Get the scene name
   */
  getSceneName(scene: Scene) {
    return Object.keys(this.scenes).find((key) => this.scenes[key] === scene);
  }

  /**
   *  Clear the scenes
   */
  clearCustom() {
    if (this.activeScene) {
      this.activeScene.deactivate();
    }

    for (const key of Object.keys(this.scenes)) {
      this.scenes[key].clearCustom();
    }

    // Clear the active scene
    this.activeScene = undefined;
  }
}
